import React, { useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Container, Paper, Radio, Stack, Typography
} from '@mui/material';
import { Download, Inventory2, Sell, Tune } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import ProductAndCategoryService from '../../logic/ProductAndCategoryService';

type ReportType = 'inventory' | 'catalog' | 'categories';

const reportOptions: { id: ReportType; title: string; description: string; icon: React.ElementType }[] = [
    { id: 'inventory', title: 'Остатки и состояния', description: 'Каждая физическая позиция, размер и текущий статус.', icon: Inventory2 },
    { id: 'catalog', title: 'Каталог и цены', description: 'Карточки товаров, стоимость и заполненные характеристики.', icon: Sell },
    { id: 'categories', title: 'Матрица категорий', description: 'Справочник характеристик и распределение товаров.', icon: Tune },
];

const setSheetLayout = (sheet: XLSX.WorkSheet, widths: number[], rows: number) => {
    sheet['!cols'] = widths.map(wch => ({ wch }));
    if (rows > 1) sheet['!autofilter'] = { ref: sheet['!ref'] || `A1:A${rows}` };
};

const GenerateDocumentComponent: React.FC = () => {
    const [reportType, setReportType] = useState<ReportType>('inventory');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const selected = useMemo(() => reportOptions.find(option => option.id === reportType)!, [reportType]);

    const generateReport = async () => {
        setLoading(true);
        setMessage('');
        try {
            const [products, categories] = await Promise.all([
                ProductAndCategoryService.getAllProductsForAdmin(),
                ProductAndCategoryService.getAllCategories(),
            ]);
            if (!products.length) throw new Error('В каталоге нет товаров для формирования отчёта.');

            const workbook = XLSX.utils.book_new();
            const now = new Date();
            const summaryRows = [
                ['ONE KNOPKA — административный отчёт'],
                ['Тип', selected.title],
                ['Сформирован', now.toLocaleString('ru-RU')],
                ['Товаров', products.length],
                ['Физических позиций', products.reduce((sum, product) => sum + product.positions.length, 0)],
                ['Источник', 'Backend API'],
            ];
            const summary = XLSX.utils.aoa_to_sheet(summaryRows);
            summary['!cols'] = [{ wch: 26 }, { wch: 48 }];
            XLSX.utils.book_append_sheet(workbook, summary, 'О документе');

            if (reportType === 'inventory') {
                const rows = products.flatMap(product => product.positions.map(position => ({
                    'Товар': product.name, 'ID товара': product.id, 'ID позиции': position.positionId,
                    'Размер': position.size, 'Статус': position.status || 'Не указано', 'Цена, ₽': product.price,
                })));
                const sheet = XLSX.utils.json_to_sheet(rows);
                setSheetLayout(sheet, [30, 22, 22, 12, 18, 14], rows.length + 1);
                XLSX.utils.book_append_sheet(workbook, sheet, 'Остатки');
            }

            if (reportType === 'catalog') {
                const rows = products.map(product => ({
                    'Товар': product.name, 'ID': product.id, 'Цена, ₽': product.price,
                    'Позиций': product.positions.length,
                    'Характеристики': product.categories.map(category => `${category.name}: ${category.value}`).join(' · '),
                    'Описание': product.description,
                }));
                const sheet = XLSX.utils.json_to_sheet(rows);
                setSheetLayout(sheet, [30, 22, 14, 12, 58, 64], rows.length + 1);
                XLSX.utils.book_append_sheet(workbook, sheet, 'Каталог');
            }

            if (reportType === 'categories') {
                const rows = categories.flatMap(category => category.values.map(value => ({
                    'Категория': category.name, 'Значение': value,
                    'Товаров': products.filter(product => product.categories.some(chosen =>
                        chosen.name === category.name && chosen.value === value)).length,
                    'Системное значение': value === 'Не указано' ? 'Да' : 'Нет',
                })));
                const sheet = XLSX.utils.json_to_sheet(rows);
                setSheetLayout(sheet, [28, 32, 14, 22], rows.length + 1);
                XLSX.utils.book_append_sheet(workbook, sheet, 'Категории');
            }

            const stamp = now.toISOString().slice(0, 19).replace(/[:T]/g, '-');
            XLSX.writeFile(workbook, `one-knopka-${reportType}-${stamp}.xlsx`, { compression: true });
            setMessage('Отчёт сформирован и сохранён.');
        } catch (error) {
            setMessage(error instanceof Error ? error.message : 'Не удалось сформировать отчёт.');
        } finally { setLoading(false); }
    };

    return (
        <Container maxWidth="lg" disableGutters>
            <Box component="header" sx={{ mb: 4, maxWidth: 720 }}>
                <Chip label="XLSX · актуальные данные" size="small" sx={{ mb: 2, borderRadius: 1 }} />
                <Typography variant="h4" gutterBottom>Центр отчётов</Typography>
                <Typography color="text.secondary" sx={{ fontSize: 16, lineHeight: 1.65 }}>
                    Выберите срез данных. Каждый файл получает служебный лист, дату формирования и готовые фильтры колонок.
                </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                {reportOptions.map(option => {
                    const Icon = option.icon;
                    const active = option.id === reportType;
                    return (
                        <Paper key={option.id} component="button" type="button" onClick={() => setReportType(option.id)}
                            elevation={0} sx={{ textAlign: 'left', p: 2.5, borderRadius: 3, cursor: 'pointer', color: 'inherit',
                                border: '1px solid', borderColor: active ? 'secondary.main' : 'rgba(24,49,83,.12)',
                                bgcolor: active ? 'rgba(212,95,60,.07)' : 'background.paper',
                                '&:hover': { transform: 'translateY(-2px)', borderColor: 'secondary.main' } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                                <Box sx={{ width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: 2,
                                    bgcolor: active ? 'secondary.main' : 'background.alternate', color: active ? 'white' : 'primary.main' }}>
                                    <Icon />
                                </Box>
                                <Radio checked={active} value={option.id} color="secondary" />
                            </Stack>
                            <Typography variant="h6" fontWeight={700} gutterBottom>{option.title}</Typography>
                            <Typography color="text.secondary" variant="body2" lineHeight={1.55}>{option.description}</Typography>
                        </Paper>
                    );
                })}
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, bgcolor: 'primary.main', color: 'white' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2}>
                    <Box><Typography variant="h6" fontWeight={700}>{selected.title}</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,.68)' }}>Формат XLSX, подходит для Excel и Google Sheets.</Typography></Box>
                    <Button variant="contained" color="secondary" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Download />}
                        onClick={generateReport} disabled={loading} sx={{ minWidth: 210 }}>Сформировать отчёт</Button>
                </Stack>
            </Paper>
            {message && <Alert severity={message.includes('сформирован') ? 'success' : 'error'} sx={{ mt: 2 }}>{message}</Alert>}
        </Container>
    );
};

export default GenerateDocumentComponent;
